import React, { useState } from "react";
import { db } from "../firebaseConfig";
import { collection, addDoc, Timestamp, doc, updateDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useNavigate, Link } from "react-router-dom";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import GiveUsYourCarForm from "./GiveUsYourCarForm";  // Import the personal vehicle form

// Import background image
import back7 from "./back1.png";

const SingleListingPage = () => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [activeForm, setActiveForm] = useState("investment"); // State to toggle forms

  const [formData, setFormData] = useState({
    carMake: "",
    carModel: "",
    year: "1990",
    price: 15000,
    initialInvestment: "",
    serviceFee: 0,
    totalDue: 0,
    additionalDetails: "",
  });

  const [pictures, setPictures] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  const priceOptions = Array.from(
    { length: (500000 - 15000) / 5000 + 1 },
    (_, i) => 15000 + i * 5000
  );

  const yearOptions = [];
  for (let year = 1990; year <= 2028; year++) {
    yearOptions.push(year);
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "initialInvestment") {
      let investment = parseFloat(value) || 0;
      if (investment > formData.price) {
        investment = formData.price;
      }
      const serviceFee = investment * 0.035;
      const totalDue = investment + serviceFee;

      if (investment < formData.price * 0.1) {
        setErrorMessage(
          `Initial investment must be at least 10% of the price ($${(
            formData.price * 0.1
          ).toFixed(2)})`
        );
      } else {
        setErrorMessage("");
      }

      setFormData((prev) => ({
        ...prev,
        initialInvestment: investment.toString(),
        serviceFee,
        totalDue,
      }));
    } else if (name === "price") {
      const price = parseFloat(value) || 0;

      if (parseFloat(formData.initialInvestment) < price * 0.1) {
        setErrorMessage(
          `Initial investment must be at least 10% of the price ($${(
            price * 0.1
          ).toFixed(2)})`
        );
      } else {
        setErrorMessage("");
      }

      setFormData((prev) => ({
        ...prev,
        price,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handlePicturesChange = (e) => {
    const files = Array.from(e.target.files);
    setPictures(files);
  };

  const handleSubmit = async () => {
    const authInstance = getAuth();
    const currentUser = authInstance.currentUser;
    if (!currentUser) {
      alert("User not logged in.");
      return;
    }

    const userID = currentUser.uid;
    const userEmail = currentUser.email;

    if (!stripe || !elements) {
      alert("Stripe has not loaded properly.");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      alert("Please enter your card details.");
      return;
    }

    try {
      const paymentIntentResponse = await fetch(
        "https://us-central1-gearshare-41e19.cloudfunctions.net/api/create-payment-intent",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: Math.round(formData.totalDue * 100),
          }),
        }
      );
      const { clientSecret } = await paymentIntentResponse.json();

      const { paymentIntent, error: confirmError } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              email: userEmail,
            },
          },
        });

      if (confirmError) {
        alert(`Payment failed: ${confirmError.message}`);
        return;
      }

      if (paymentIntent.status !== "succeeded") {
        alert("Payment not completed. Please try again.");
        return;
      }

      const listingData = {
        carName: `${formData.carMake} ${formData.carModel}`.trim(),
        year: parseInt(formData.year, 10) || 0,
        totalValue: formData.price || 0,
        initialInvestment: parseFloat(formData.initialInvestment) || 0,
        serviceFee: parseFloat(formData.serviceFee) || 0,
        totalDue: parseFloat(formData.totalDue) || 0,
        additionalDetails: formData.additionalDetails,
        pictures: pictures.map((file) => file.name),
        status: "Pending Approval",
        createdAt: Timestamp.now(),
        userID,
        userEmail,
        paymentIntentId: paymentIntent.id,
      };

      const listingRef = await addDoc(collection(db, "listings"), listingData);
      await updateDoc(doc(db, "listings", listingRef.id), {
        listingID: listingRef.id,
      });

      // Save the payment data to Firestore
      const paymentData = {
        paymentID: paymentIntent.id,
        amount: formData.totalDue,
        userID,
        userEmail,
        carName: listingData.carName,
        status: "Pending",
        createdAt: Timestamp.now(),
      };

      await addDoc(collection(db, "payments"), paymentData);

      alert(
        `Your listing has been submitted for approval! Listing ID: ${listingRef.id}`
      );
      navigate("/requests");
    } catch (error) {
      console.error("Error submitting listing or processing payment:", error);
      alert("Something went wrong. Please try again!");
    }
  };

  const freeDays =
    formData.price && formData.initialInvestment
      ? Math.floor(
          (parseFloat(formData.initialInvestment) / formData.price) / 0.033
        )
      : 0;

  return (
    <div
      style={{
        backgroundImage: `url(${back7})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          maxWidth: "800px",
          margin: "auto",
          padding: "20px",
          backgroundColor: "rgba(2, 17, 65, 0)",
        }}
      >
        {/* Toggle Buttons */}
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <button
            onClick={() => setActiveForm("investment")}
            style={{
              marginRight: "10px",
              padding: "10px",
              backgroundColor: "#007bff",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
            }}
          >
            New Investment Vehicle
          </button>
          <button
            onClick={() => setActiveForm("personal")}
            style={{
              padding: "10px",
              backgroundColor: "#28a745",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
            }}
          >
            List a Personal Vehicle
          </button>
        </div>

        {/* Conditional Rendering of Forms */}
        {activeForm === "investment" ? (
          <>
            <h1>Create a Vehicle Listing</h1>
            <form
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                padding: "20px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                backgroundColor: "#fff",
              }}
            >
              {/* ... All existing form fields and elements from SingleListingPage ... */}
              <div>
                <label style={{ color: "black" }}>Car Make:</label>
                <input
                  type="text"
                  name="carMake"
                  value={formData.carMake}
                  onChange={handleInputChange}
                  placeholder="Enter car make"
                  maxLength={15}
                  required
                />
              </div>

              <div>
                <label style={{ color: "black" }}>Car Model:</label>
                <input
                  type="text"
                  name="carModel"
                  value={formData.carModel}
                  onChange={handleInputChange}
                  placeholder="Enter car model"
                  maxLength={15}
                  required
                />
              </div>

              <div>
                <label style={{ color: "black" }}>Year:</label>
                <select
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                  required
                >
                  <option value="" disabled>Select year</option>
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ color: "black" }}>Price:</label>
                <select
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                >
                  {priceOptions.map((price) => (
                    <option key={price} value={price}>
                      ${price.toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ color: "black" }}>Initial Investment:</label>
                <input
                  type="number"
                  name="initialInvestment"
                  value={formData.initialInvestment}
                  onChange={handleInputChange}
                  placeholder="Enter initial investment"
                  required
                />
              </div>

              <div>
                <label style={{ color: "black" }}>Upload Example Pictures:</label>
                <br />
                <input type="file" multiple onChange={handlePicturesChange} />
              </div>

              <div>
                <label style={{ color: "black" }}>Additional Details:</label>
                <textarea
                  name="additionalDetails"
                  value={formData.additionalDetails}
                  onChange={handleInputChange}
                  placeholder="Enter additional details about the vehicle"
                  rows="5"
                  style={{ width: "100%" }}
                ></textarea>
              </div>

              {errorMessage && (
                <p style={{ color: "red", fontWeight: "bold" }}>{errorMessage}</p>
              )}

              <div
                style={{
                  marginTop: "20px",
                  padding: "10px",
                  backgroundColor: "#f1f1f1",
                  borderRadius: "5px",
                }}
              >
                <p style={{ marginTop: "10px", color: "#555", fontSize: "0.9em" }}>
                  Note: Please give your best guess as to how much the vehicle you want us
                  to invest in is priced at. The investment vehicle may slightly differ in
                  year or model. You agree that GearShare picks investments based on profit
                  potential first and foremost. You will be rejected if the value of the
                  car is too far off from your initial investment or car details are unspecified.
                  You can also be rejected if the vehicle is too hard to acquire due to limited
                  availability. Processing fee is non-refundable.
                  <Link to="/terms-of-service" style={{ color: "#007bff", textDecoration: "underline" }}>
                    Click here for more details on investment criteria.
                  </Link>
                </p>
                <h3 style={{ color: "black" }}>Summary</h3>
                <p style={{ color: "black" }}>
                  <strong>Vehicle Value:</strong> ${formData.price.toLocaleString()}
                </p>
                <p style={{ color: "black" }}>
                  <strong>Processing Fee:</strong> ${formData.serviceFee.toFixed(2)}
                </p>
                <p style={{ color: "black" }}>
                  <strong>Total Due:</strong> ${formData.totalDue.toFixed(2)}
                </p>
                <p style={{ fontStyle: "italic", marginTop: "10px", color: "#323b45" }}>
                  You will receive {freeDays} Free Rental Days per year.
                </p>
              </div>

              <h3 style={{ color: "black" }}>Enter Payment Information</h3>
              <div style={{ border: "1px solid #ddd", padding: "10px", borderRadius: "8px" }}>
                <CardElement
                  options={{
                    hidePostalCode: false,
                    style: {
                      base: {
                        fontSize: "16px",
                        color: "#424770",
                        "::placeholder": { color: "#aab7c4" },
                      },
                      invalid: { color: "#9e2146" },
                    },
                  }}
                />
              </div>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={!!errorMessage}
                style={{
                  padding: "12px",
                  backgroundColor: errorMessage ? "#ccc" : "#007bff",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: errorMessage ? "not-allowed" : "pointer",
                }}
              >
                Submit Listing and Payment
              </button>
            </form>
          </>
        ) : (
          <GiveUsYourCarForm />
        )}
      </div>
    </div>
  );
};

export default SingleListingPage;
